import React from "react";
import useTranslations from "../../hooks/useTranslations";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import { getAvatarColor, getAvatarInitials } from "../../utilities/avatar";
import { iconsList } from "../../utilities/iconsList";
import { ImagePresetCollection } from "../../utilities/image";
import LinkWithArrow from "../LinkWithArrow";
import { Picture } from "../Picture";

interface CustomReviewProps {
  author: string;
  rating: number;
  jobPosition?: string;
  avatar?: SanityImageSourceExt;
  reviewUrl?: string;
  text?: string;
}

const imagePresets: ImagePresetCollection = {
  default: {
    height: 64,
    width: 64
  },
  lg: {
    height: 80,
    width: 80
  }
};

const CustomReview = ({
  author,
  avatar,
  rating,
  jobPosition,
  text = "",
  reviewUrl
}: CustomReviewProps) => {
  const translations = useTranslations();

  return (
    <div className="bg-white text-brand-primary-40 rounded-lg p-8 shadow-lg min-h-56">
      <div className="flex gap-4 items-center">
        {avatar ? (
          <div className="shrink-0">
            <Picture image={avatar} presets={imagePresets} />
          </div>
        ) : (
          <div
            className="size-16 lg:size-20 shrink-0 rounded-full inline-flex items-center justify-center text-2xl lg:text-4xl font-medium text-white"
            style={{ background: getAvatarColor(author) }}
          >
            {getAvatarInitials(author)}
          </div>
        )}
        <div className="space-y-2">
          <div className="text-lg">
            <h5 className="inline font-bold">{author}</h5>
            {author && jobPosition && <span> - </span>}
            <p className="inline text-brand-primary-40 font-medium">{jobPosition}</p>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, index) => {
              const boxIsFilled = rating >= index + 1;
              const starUrl = boxIsFilled
                ? iconsList.reviewStarFilled
                : iconsList.reviewStarEmpty;
              return (
                <svg className={"w-5 h-5"}>
                  <use xlinkHref={starUrl} />
                </svg>
              );
            })}
          </div>
        </div>
      </div>
      <p className="mt-3 text-inherit">{`"${text}"`}</p>
      {reviewUrl && (
        <LinkWithArrow
          linkInfo={{
            isExternalLink: true,
            url: reviewUrl,
            title: translations.reviewsSeeFullReviewLinkText ?? ""
          }}
          colorClasses="text-sky-600 border-sky-600 text-base inline-flex"
        />
      )}
    </div>
  );
};

export default CustomReview;

